// The free-trial notice under the header: a countdown while the trial runs (linking to
// Billing), "cancelled" with Undo, the look-only explanation once it has ended (with the
// deletion date), and a failed payment with the way to fix it.
// In the last week and after the end, "Ask us for a quote" opens the help centre's contact form.
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { PAGES, pageLink, type TrialInfo } from "@convenor/shared";
import { api, ApiError } from "../api/client";
import { qk } from "../api/keys";
import { useMeta } from "../api/meta";
import { Button } from "../ui/Button";
import { Notice } from "../ui/Notice";
import { useToast } from "../ui/Toast";

// Brussels time, so the text is the same on every machine.
const longDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Brussels" });

const quoteLink = <Link to={pageLink("help", {}, { hash: "contact" })}>Ask us for a quote</Link>;

export function TrialBanner({ trial }: { trial: TrialInfo | null }) {
  const { appName } = useMeta();
  // Waiting for payment details: RequireAuth keeps the person on "Add your payment details".
  if (!trial || trial.phase === "none" || trial.phase === "awaiting_payment") return null;
  if (trial.phase === "payment_failed") {
    return (
      <Notice kind="danger">
        Your payment did not go through. <Link to={PAGES.settingsBilling}>Update your payment details</Link>.
      </Notice>
    );
  }
  if (trial.phase === "cancelled" && trial.daysLeft) {
    return (
      <Notice kind="info">
        Your trial is cancelled. Nothing will be charged. <UndoCancel />
      </Notice>
    );
  }
  if (trial.phase === "active") {
    const left = trial.daysLeft ?? 0;
    const days = left === 1 ? "1 day" : `${left} days`;
    const lastWeek = left <= 7;
    return (
      <Notice kind={lastWeek ? "warning" : "info"}>
        <Link to={PAGES.settingsBilling}>{days} left in your free trial</Link>. Your data stays yours when you continue.
        {lastWeek ? <> {quoteLink}.</> : null}
      </Notice>
    );
  }
  return (
    <Notice kind="danger">
      Your free trial has ended, so {appName} is read-only. Your data is kept until {trial.deleteAt ? longDate(trial.deleteAt) : "further notice"}. {quoteLink} to keep working.
    </Notice>
  );
}

/** Undo a cancelled trial (owners; POST /api/trial/undo-cancel). */
function UndoCancel() {
  const qc = useQueryClient();
  const toast = useToast();
  const undo = useMutation({
    mutationFn: () => api.post<unknown>("/api/trial/undo-cancel", {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
    onError: (e) => toast(e instanceof ApiError ? e.message : "That did not work. Try again."),
  });
  return (
    <Button variant="secondary" busy={undo.isPending} busyLabel="Undoing…" onClick={() => undo.mutate()}>
      Undo
    </Button>
  );
}
